import { useState, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import {
  ThumbsUp,
  ThumbsDown,
  UserCheck,
  ChevronRight,
  ChevronDown,
  CheckCircle2,
  XCircle,
  Download,
  ExternalLink,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  useAdminFeedbacks,
  useAdminFeedbacksPagination,
  useAdminFeedbacksFilters,
  useMarkFeedbackAddressed,
  useUnmarkFeedbackAddressed,
} from '@/hooks';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { adminFeedbackService } from '@/services/adminFeedback.service';
import type { AdminFeedbackItemDto } from '@/types/dto.types';

const ALL_VALUE = '__all__';

const FEEDBACK_TYPE_LABELS: Record<string, string> = {
  intent_matches: 'Intent matches',
  tone_was_right: 'Tone was right',
  sources_used_were_right: 'Sources were right',
  intent_doesnt_match: "Intent doesn't match",
  tone_was_wrong: 'Tone was wrong',
  sources_used_were_wrong: 'Sources were wrong',
};

const POSITIVE_TYPES = ['intent_matches', 'tone_was_right', 'sources_used_were_right'];

function isPositiveFeedback(sentiment: string | null, feedbackType: string | null): boolean {
  return sentiment === 'positive' || (feedbackType != null && POSITIVE_TYPES.includes(feedbackType));
}

function feedbackTypeLabel(feedbackType: string): string {
  return FEEDBACK_TYPE_LABELS[feedbackType] ?? feedbackType.replace(/_/g, ' ');
}

function formatDate(date: string): string {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function MarkdownBlock({ text }: { text: string }) {
  const html = useMemo(
    () => DOMPurify.sanitize(marked(text, { async: false })),
    [text],
  );
  return (
    <div
      className='prose prose-sm max-w-none text-sm [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:mb-2 [&_ol]:mb-2 [&_li]:my-0.5 [&_h1]:text-base [&_h2]:text-sm [&_h3]:text-sm [&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-xs [&_pre]:rounded [&_pre]:bg-muted [&_pre]:p-2 [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_a]:text-primary [&_a]:underline'
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function ContextBlock({ label, text }: { label: string; text: string | null }) {
  if (!text) {
    return (
      <div className='flex flex-col gap-1.5'>
        <span className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
          {label}
        </span>
        <span className='text-sm text-muted-foreground'>—</span>
      </div>
    );
  }
  return (
    <div className='flex flex-col gap-1.5'>
      <span className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
        {label}
      </span>
      <MarkdownBlock text={text} />
    </div>
  );
}

interface AddressDialogProps {
  feedbackId: string | null;
  onClose: () => void;
}

function AddressDialog({ feedbackId, onClose }: AddressDialogProps) {
  const [note, setNote] = useState('');
  const markAddressed = useMarkFeedbackAddressed();

  function handleSubmit() {
    if (!feedbackId || !note.trim()) return;
    markAddressed.mutate(
      { id: feedbackId, note },
      {
        onSuccess: () => {
          setNote('');
          onClose();
        },
      },
    );
  }

  function handleOpenChange(open: boolean) {
    if (!open) {
      setNote('');
      onClose();
    }
  }

  return (
    <Dialog open={!!feedbackId} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Mark as addressed</DialogTitle>
        </DialogHeader>
        <div className='flex flex-col gap-3 py-2'>
          <p className='text-sm text-muted-foreground'>
            Briefly describe how this feedback was addressed.
          </p>
          <Textarea
            placeholder='e.g. Updated the knowledge base with corrected intent definitions'
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={4}
            maxLength={1000}
            className='resize-none'
          />
          <p className='text-xs text-muted-foreground text-right'>{note.length}/1000</p>
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!note.trim() || markAddressed.isPending}
          >
            {markAddressed.isPending ? 'Saving…' : 'Mark addressed'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface FeedbackRowProps {
  item: AdminFeedbackItemDto;
  onMarkAddressed: (id: string) => void;
}

function FeedbackRow({ item, onMarkAddressed }: FeedbackRowProps) {
  const [open, setOpen] = useState(false);
  const unmarkAddressed = useUnmarkFeedbackAddressed();
  const positive = isPositiveFeedback(item.sentiment, item.feedbackType);
  const FEEDBACK_LIMIT = 80;

  return (
    <>
      <TableRow className='cursor-pointer' onClick={() => setOpen((v) => !v)}>
        <TableCell className='w-10'>
          {positive ? (
            <ThumbsUp className='h-4 w-4 text-green-600' />
          ) : (
            <ThumbsDown className='h-4 w-4 text-red-500' />
          )}
        </TableCell>
        <TableCell className='whitespace-normal'>
          <div className='flex flex-wrap gap-1'>
            {(item.feedbackType ?? '').split(', ').filter(Boolean).map((type) => (
              <Badge
                key={type}
                variant='outline'
                className={
                  positive
                    ? 'whitespace-nowrap border-green-200 bg-green-50 text-xs text-green-700'
                    : 'whitespace-nowrap border-red-200 bg-red-50 text-xs text-red-700'
                }
              >
                {feedbackTypeLabel(type.trim())}
              </Badge>
            ))}
          </div>
        </TableCell>
        <TableCell className='max-w-xs whitespace-normal'>
          <span className='line-clamp-2 text-muted-foreground'>
            {item.feedback
              ? item.feedback.length > FEEDBACK_LIMIT
                ? `${item.feedback.slice(0, FEEDBACK_LIMIT)}…`
                : item.feedback
              : <span className='italic'>No comment</span>}
          </span>
        </TableCell>
        <TableCell className='whitespace-normal'>
          {item.agentName ? (
            <div>
              <div className='font-medium'>{item.agentName}</div>
              {item.agentEmail && (
                <div className='text-xs text-muted-foreground'>{item.agentEmail}</div>
              )}
            </div>
          ) : (
            <span className='text-muted-foreground'>—</span>
          )}
        </TableCell>
        <TableCell className='whitespace-normal'>
          {item.brandName ?? <span className='text-muted-foreground'>—</span>}
        </TableCell>
        <TableCell>
          {item.isAddressed ? (
            <Badge variant='outline' className='gap-1 border-emerald-200 bg-emerald-50 text-xs text-emerald-700 whitespace-nowrap'>
              <CheckCircle2 className='h-3 w-3' />
              Addressed
            </Badge>
          ) : (
            <span className='text-xs text-muted-foreground'>—</span>
          )}
        </TableCell>
        <TableCell className='text-muted-foreground'>
          {formatDate(item.createdAt)}
        </TableCell>
        <TableCell className='text-muted-foreground'>
          {open ? (
            <ChevronDown className='h-4 w-4' />
          ) : (
            <ChevronRight className='h-4 w-4' />
          )}
        </TableCell>
      </TableRow>

      {open && (
        <TableRow className='bg-gray-50 hover:bg-gray-50'>
          <TableCell colSpan={8} className='px-6 py-5 whitespace-normal'>
            <div className='flex flex-col gap-5'>
              <div className='flex items-center gap-2 flex-wrap'>
                {item.ticketUrl && (
                  <a
                    href={item.ticketUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    onClick={(e) => e.stopPropagation()}
                    className='inline-flex items-center gap-1 rounded-md border border-border bg-white px-2.5 py-1 text-xs font-medium text-primary hover:bg-muted/40'
                  >
                    <ExternalLink className='h-3 w-3' />
                    View ticket
                  </a>
                )}
                {item.isHitl && (
                  <Badge variant='outline' className='gap-1 border-blue-200 bg-blue-50 text-blue-700'>
                    <UserCheck className='h-3 w-3' />
                    HITL response
                  </Badge>
                )}
                {item.isAddressed && item.addressedNote && (
                  <div className='flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800'>
                    <CheckCircle2 className='h-4 w-4 mt-0.5 shrink-0 text-emerald-600' />
                    <div>
                      <span className='font-medium'>Addressed: </span>
                      {item.addressedNote}
                      {item.addressedAt && (
                        <span className='ml-2 text-xs text-emerald-600'>
                          {formatDate(item.addressedAt)}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className='grid grid-cols-2 gap-6'>
                <div className='rounded-md border border-border bg-white p-4'>
                  <ContextBlock label='Customer query' text={item.customerQuery} />
                </div>
                <div className='rounded-md border border-border bg-white p-4'>
                  <ContextBlock label='AI response' text={item.aiMessage} />
                </div>
              </div>

              {item.feedback && item.feedback.length > FEEDBACK_LIMIT && (
                <div className='rounded-md border border-border bg-white p-4'>
                  <ContextBlock label="Agent's comment" text={item.feedback} />
                </div>
              )}

              <div className='flex gap-2' onClick={(e) => e.stopPropagation()}>
                {item.isAddressed ? (
                  <Button
                    variant='outline'
                    size='sm'
                    className='gap-1.5 text-muted-foreground'
                    disabled={unmarkAddressed.isPending}
                    onClick={() => unmarkAddressed.mutate(item.id)}
                  >
                    <XCircle className='h-3.5 w-3.5' />
                    {unmarkAddressed.isPending ? 'Clearing…' : 'Unmark addressed'}
                  </Button>
                ) : (
                  <Button
                    variant='outline'
                    size='sm'
                    className='gap-1.5 text-emerald-700 border-emerald-300 hover:bg-emerald-50'
                    onClick={() => onMarkAddressed(item.id)}
                  >
                    <CheckCircle2 className='h-3.5 w-3.5' />
                    Mark as addressed
                  </Button>
                )}
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function FeedbacksTable() {
  const { data, isLoading } = useAdminFeedbacks();
  const [{ page, limit }, setPagination] = useAdminFeedbacksPagination();
  const [addressingId, setAddressingId] = useState<string | null>(null);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  return (
    <div className='flex flex-col gap-3'>
      <div className='text-sm text-muted-foreground'>
        {total} feedback{total !== 1 ? 's' : ''}
      </div>
      <Table>
          <TableHeader>
            <TableRow>
              <TableHead className='w-10' />
              <TableHead>Reason</TableHead>
              <TableHead>Agent comment</TableHead>
              <TableHead>Agent</TableHead>
              <TableHead>Brand</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className='w-8' />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className='h-24 text-center text-muted-foreground'>
                  Loading…
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className='h-24 text-center text-muted-foreground'>
                  No feedbacks found.
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <FeedbackRow
                  key={item.id}
                  item={item}
                  onMarkAddressed={setAddressingId}
                />
              ))
            )}
          </TableBody>
      </Table>

      {totalPages > 1 && (
        <div className='flex items-center justify-end gap-2'>
          <Button
            variant='outline'
            size='sm'
            disabled={page <= 1}
            onClick={() => setPagination({ page: page - 1, limit })}
          >
            Previous
          </Button>
          <span className='text-sm text-muted-foreground'>
            {page} / {totalPages}
          </span>
          <Button
            variant='outline'
            size='sm'
            disabled={page >= totalPages}
            onClick={() => setPagination({ page: page + 1, limit })}
          >
            Next
          </Button>
        </div>
      )}

      <AddressDialog
        feedbackId={addressingId}
        onClose={() => setAddressingId(null)}
      />
    </div>
  );
}

function FeedbackFilters() {
  const { isLoading } = useAdminFeedbacks();
  const [filters, setFilters] = useAdminFeedbacksFilters();
  const [, setPagination] = useAdminFeedbacksPagination();
  const [accountIdInput, setAccountIdInput] = useState(filters.accountId ?? '');

  function applyFilters(overrides: Partial<typeof filters>) {
    setFilters((prev) => ({ ...prev, ...overrides }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  }

  function handleAccountIdSearch() {
    applyFilters({ accountId: accountIdInput.trim() || undefined });
  }

  function handleSentimentChange(value: string) {
    applyFilters({ sentiment: value === ALL_VALUE ? undefined : value });
  }

  function handleFeedbackTypeChange(value: string) {
    applyFilters({ feedbackType: value === ALL_VALUE ? undefined : value });
  }

  function handleAddressedChange(value: string) {
    applyFilters({
      isAddressed: value === ALL_VALUE ? undefined : value === 'true',
    });
  }

  function handleReset() {
    setAccountIdInput('');
    setFilters({});
    setPagination((prev) => ({ ...prev, page: 1 }));
  }

  const addressedValue =
    filters.isAddressed === true ? 'true' : filters.isAddressed === false ? 'false' : ALL_VALUE;

  return (
    <div className='rounded-lg border border-border bg-white px-4 py-3'>
      <div className='flex flex-wrap items-center gap-2'>
        <Input
          value={accountIdInput}
          onChange={(e) => setAccountIdInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAccountIdSearch()}
          onBlur={handleAccountIdSearch}
          placeholder='Account ID'
          disabled={isLoading}
          className='h-8 w-36 text-sm'
        />

        <div className='h-5 w-px bg-border' />

        <Select value={filters.sentiment ?? ALL_VALUE} onValueChange={handleSentimentChange} disabled={isLoading}>
          <SelectTrigger size='sm' className='w-36 border border-border text-sm'>
            <SelectValue placeholder='Sentiment' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>All sentiments</SelectItem>
            <SelectItem value='positive'>Positive</SelectItem>
            <SelectItem value='negative'>Negative</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filters.feedbackType ?? ALL_VALUE} onValueChange={handleFeedbackTypeChange} disabled={isLoading}>
          <SelectTrigger size='sm' className='w-44 border border-border text-sm'>
            <SelectValue placeholder='Reason' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>All reasons</SelectItem>
            {Object.entries(FEEDBACK_TYPE_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={addressedValue} onValueChange={handleAddressedChange} disabled={isLoading}>
          <SelectTrigger size='sm' className='w-40 border border-border text-sm'>
            <SelectValue placeholder='Status' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>All statuses</SelectItem>
            <SelectItem value='false'>Not addressed</SelectItem>
            <SelectItem value='true'>Addressed</SelectItem>
          </SelectContent>
        </Select>

        <div className='h-5 w-px bg-border' />

        <DateRangePicker
          inline
          value={
            filters.fromDate || filters.toDate
              ? {
                  startDate: filters.fromDate ? new Date(filters.fromDate) : undefined,
                  endDate: filters.toDate ? new Date(filters.toDate) : undefined,
                }
              : undefined
          }
          onChange={(range) =>
            applyFilters({
              fromDate: range.startDate ? range.startDate.toISOString().split('T')[0] : undefined,
              toDate: range.endDate ? range.endDate.toISOString().split('T')[0] : undefined,
            })
          }
          placeholder={{ start: 'From', end: 'To' }}
        />

        <div className='h-5 w-px bg-border' />

        <Button variant='ghost' size='sm' onClick={handleReset} disabled={isLoading} className='h-8 text-muted-foreground'>
          Reset
        </Button>
      </div>
    </div>
  );
}

async function downloadExcel(filters: Omit<Parameters<typeof adminFeedbackService.list>[0], 'page' | 'limit'>) {
  const result = await adminFeedbackService.list({ ...filters, page: 1, limit: 10000 });
  const rows = result.items.map((item) => ({
    Date: formatDate(item.createdAt),
    Brand: item.brandName ?? '',
    Agent: item.agentName ?? '',
    'Agent Email': item.agentEmail ?? '',
    Sentiment: item.sentiment ?? '',
    Reason: item.feedbackType,
    "Agent's Comment": item.feedback,
    'Customer Query': item.customerQuery ?? '',
    'AI Response': item.aiMessage ?? '',
    HITL: item.isHitl ? 'Yes' : 'No',
    Addressed: item.isAddressed ? 'Yes' : 'No',
    'Addressed Note': item.addressedNote ?? '',
    'Addressed At': item.addressedAt ? formatDate(item.addressedAt) : '',
    'Account ID': item.accountId,
    'Trace ID': item.traceId ?? '',
    'Ticket URL': item.ticketUrl ?? '',
  }));

  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Feedbacks');
  XLSX.writeFile(wb, `feedbacks-${new Date().toISOString().split('T')[0]}.xlsx`);
}

function ExportButton() {
  const [filters] = useAdminFeedbacksFilters();
  const [isExporting, setIsExporting] = useState(false);

  async function handleExport() {
    setIsExporting(true);
    try {
      await downloadExcel(filters);
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <Button variant='outline' size='sm' onClick={handleExport} disabled={isExporting} className='gap-1.5'>
      <Download className='h-4 w-4' />
      {isExporting ? 'Exporting…' : 'Export Excel'}
    </Button>
  );
}

export default function FeedbacksPage() {
  return (
    <div className='flex h-full flex-col gap-6 p-4'>
      <div className='flex items-center justify-between'>
          <div>
            <h1 className='text-base font-semibold'>Agent Feedbacks</h1>
            <p className='text-xs text-muted-foreground'>
              Feedbacks submitted by agents via the extension.
            </p>
          </div>
        <ExportButton />
      </div>

      <FeedbackFilters />
      <FeedbacksTable />
    </div>
  );
}
